import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Typography,
} from '@mui/material';
import ConfirmDialog from '../common/ConfirmDialog';
import { Link as RouterLink } from 'react-router-dom';
import { AuthService } from '../../services/auth.service';
import { FxRevaluationService } from '../../services/fxRevaluation.service';
import { FxExposure, FxRevaluationRun, RevaluationPreview } from '../../interfaces/fx.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';

/** Last day of the previous month in the viewer's local calendar (the usual revaluation date). */
/** True when `iso` (yyyy-MM-dd) is the last day of its month: the only dates a run accepts. */
export const isMonthEnd = (iso: string): boolean => {
    const [y, m, d] = iso.split('-').map(Number);
    return !!y && !!m && !!d && d === new Date(y, m, 0).getDate();
};

export const lastMonthEnd = (now: Date = new Date()): string =>
    toLocalIsoDate(new Date(now.getFullYear(), now.getMonth(), 0));

const STATUS_LABEL: Record<string, string> = {
    PENDING: 'Pending', POSTED: 'Posted', FAILED: 'Failed', REVERSED_MANUALLY: 'Undone',
};

export const ITEM_TYPE_LABEL: Record<string, string> = {
    EXPORT_RECEIVABLE: 'Export receivable', BANK_FC: 'Foreign-currency bank',
    SALES_INVOICE_FC: 'Sales invoice (FC)', SUPPLIER_BILL_FC: 'Supplier bill (FC)',
};

const JournalLink: React.FC<{ id?: string | null; label: string }> = ({ id, label }) =>
    id ? <Link component={RouterLink} to={`/accounting/journal-entries/${id}`}>{label}</Link> : <span>-</span>;

/**
 * Unrealised FX revaluation: pick an as-of date, preview what would be posted (nothing is posted
 * by previewing), run it, and undo a run while its periods are open. A run posts one entry dated
 * as-of and an automatic reversal dated the next day, so realised FX keeps using the booking rate.
 */
const FxRevaluation: React.FC = () => {
    const toast = useToast();
    const canManageAccounts = usePermission(PERMISSIONS.ACCOUNT_MANAGE);
    const canPostJournals = usePermission(PERMISSIONS.JOURNAL_ENTRY_POST);
    const canRun = canManageAccounts && canPostJournals;
    const institutionId = AuthService.getSelectedInstitution()!;

    const [asOf, setAsOf] = useState(lastMonthEnd());
    const [preview, setPreview] = useState<RevaluationPreview | null>(null);
    const [exposure, setExposure] = useState<FxExposure | null>(null);
    const [runs, setRuns] = useState<FxRevaluationRun[]>([]);
    const [busy, setBusy] = useState(false);
    const [confirmRun, setConfirmRun] = useState(false);
    const [undoTarget, setUndoTarget] = useState<FxRevaluationRun | null>(null);
    const [undoLosses, setUndoLosses] = useState<string[]>([]);

    const loadRuns = useCallback(async () => {
        try {
            setRuns(await FxRevaluationService.runs(institutionId));
        } catch (e) {
            toast.showApiError(e, 'Failed to load revaluation history');
        }
    }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadRuns(); }, [loadRuns]);

    const doPreview = async () => {
        if (!asOf) return;
        setBusy(true);
        try {
            const [p, x] = await Promise.all([
                FxRevaluationService.preview(institutionId, asOf),
                FxRevaluationService.exposure(institutionId, asOf),
            ]);
            setPreview(p);
            setExposure(x);
        } catch (e) {
            toast.showApiError(e, 'Failed to preview the revaluation');
        } finally {
            setBusy(false);
        }
    };

    const doRun = async () => {
        setConfirmRun(false);
        setBusy(true);
        try {
            const done = await FxRevaluationService.execute(institutionId, asOf);
            toast.showSuccess('Revaluation posted');
            (done?.warnings ?? []).forEach(w => toast.showWarning(w));
            setPreview(null);
            setExposure(null);
            await loadRuns();
        } catch (e) {
            toast.showApiError(e, 'Failed to run the revaluation');
        } finally {
            setBusy(false);
        }
    };

    // Polish 5: the run detail names the banks an undo would make impossible to revalue again for the date.
    const askUndo = async (run: FxRevaluationRun) => {
        let losses: string[] = [];
        try {
            losses = (await FxRevaluationService.run(institutionId, run.id))?.undoLossWarnings ?? [];
        } catch {
            // the server refuses an unacknowledged undo anyway and names the banks in its message
        }
        setUndoLosses(losses);
        setUndoTarget(run);
    };

    const doUndo = async (run: FxRevaluationRun) => {
        setUndoTarget(null);
        setBusy(true);
        try {
            await FxRevaluationService.undo(institutionId, run.id, undoLosses.length > 0);
            toast.showSuccess('Revaluation undone');
            await loadRuns();
        } catch (e) {
            toast.showApiError(e, 'Failed to undo the revaluation');
        } finally {
            setBusy(false);
        }
    };

    const missing = preview?.missingRateCurrencies ?? [];
    const monthEnd = isMonthEnd(asOf);
    const canPost = canRun && monthEnd && !!preview && preview.items.length > 0 && missing.length === 0 && !preview.alreadyRun;

    return (
        <Box data-help-page="accounting.fxReval.page" sx={{ p: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h5">FX revaluation</Typography>
            <Typography variant="body2" color="text.secondary">
                Revalues open foreign-currency items (export receivables, foreign-currency bank balances, sales invoices and supplier bills) at the closing rate (the latest rate on or before the
                as-of date) and books the unrealised gain or loss. The entry is reversed automatically the next day, so
                realisation still measures realised FX against the booking rate.
            </Typography>

            <Box data-help-section="accounting.fxReval.controls" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <TextField data-help="accounting.fxReval.asOf" label="As of" type="date" size="small" value={asOf} InputLabelProps={{ shrink: true }}
                    onChange={e => { setAsOf(e.target.value); setPreview(null); setExposure(null); }}
                    sx={{ flex: '1 1 180px', maxWidth: 220 }} />
                <Button data-help="accounting.fxReval.preview" variant="outlined" disabled={busy || !asOf} onClick={doPreview}>Preview</Button>
                {canRun && <Button data-help="accounting.fxReval.run" variant="contained" disabled={busy || !canPost} onClick={() => setConfirmRun(true)}>Run revaluation</Button>}
            </Box>

            {!monthEnd && asOf && (
                <Alert severity="info">Runs are month-end entries: pick the last day of a month to run. Preview works for any date.</Alert>
            )}
            {preview && (
                <>
                    {missing.length > 0 && (
                        <Alert severity="error">
                            No exchange rate on or before {formatDate(preview.asOfDate)} for: {missing.join(', ')}.{' '}
                            <Link component={RouterLink} to="/accounting/exchange-rates">Add the rate(s)</Link> first.
                        </Alert>
                    )}
                    {preview.alreadyRun && (
                        <Alert severity="info">A revaluation run for {formatDate(preview.asOfDate)} already exists. Undo it to run again.</Alert>
                    )}
                    {preview.warnings.map(w => <Alert key={w} severity="warning">{w}</Alert>)}
                    <Paper>
                        <TableContainer>
                            <Table data-help-section="accounting.fxReval.previewTable" size="small" aria-label="Revaluation preview">
                                <TableHead>
                                    <TableRow>
                                        <TableCell data-help="accounting.fxReval.preview.col.item">Item</TableCell>
                                        <TableCell data-help="accounting.fxReval.preview.col.currency">Currency</TableCell>
                                        <TableCell data-help="accounting.fxReval.preview.col.foreign" align="right">Open foreign</TableCell>
                                        <TableCell data-help="accounting.fxReval.preview.col.carrying" align="right">Carrying (BDT)</TableCell>
                                        <TableCell data-help="accounting.fxReval.preview.col.rate" align="right">Closing rate</TableCell>
                                        <TableCell data-help="accounting.fxReval.preview.col.revalued" align="right">Revalued (BDT)</TableCell>
                                        <TableCell data-help="accounting.fxReval.preview.col.gainLoss" align="right">Gain / (loss)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {preview.items.map(i => (
                                        <TableRow key={`${i.itemType}-${i.refId}`}>
                                            <TableCell>
                                                {i.refNumber ?? i.refId}
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {ITEM_TYPE_LABEL[i.itemType] ?? i.itemType}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{i.currencyCode}</TableCell>
                                            <TableCell align="right">{formatMoney(i.openForeign)}</TableCell>
                                            <TableCell align="right">{formatMoney(i.carryingBase)}</TableCell>
                                            <TableCell align="right">{Number(i.rate).toFixed(4)}</TableCell>
                                            <TableCell align="right">{formatMoney(i.revaluedBase)}</TableCell>
                                            <TableCell align="right">{formatMoney(i.gainLoss)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {preview.items.length === 0 && (
                                        <TableRow><TableCell colSpan={7} align="center">No open foreign-currency items with a rate.</TableCell></TableRow>
                                    )}
                                    {preview.items.length > 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="right"><strong>Total gain / (loss)</strong></TableCell>
                                            <TableCell align="right">
                                                <strong>{formatMoney(preview.totalGain - preview.totalLoss)}</strong>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </>
            )}

            {exposure && (
                <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>FX exposure as of {formatDate(exposure.asOfDate)}</Typography>
                    <TableContainer>
                        <Table data-help-section="accounting.fxReval.exposureTable" size="small" aria-label="FX exposure">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="accounting.fxReval.exposure.col.currency">Currency</TableCell>
                                    <TableCell data-help="accounting.fxReval.exposure.col.items" align="right">Items</TableCell>
                                    <TableCell data-help="accounting.fxReval.exposure.col.foreign" align="right">Open foreign</TableCell>
                                    <TableCell data-help="accounting.fxReval.exposure.col.carrying" align="right">Carrying (BDT)</TableCell>
                                    <TableCell data-help="accounting.fxReval.exposure.col.rate" align="right">Rate</TableCell>
                                    <TableCell data-help="accounting.fxReval.exposure.col.current" align="right">At current rate (BDT)</TableCell>
                                    <TableCell data-help="accounting.fxReval.exposure.col.difference" align="right">Difference</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {exposure.lines.map(l => (
                                    <TableRow key={l.currencyCode}>
                                        <TableCell>{l.currencyCode}</TableCell>
                                        <TableCell align="right">{l.itemCount}</TableCell>
                                        <TableCell align="right">{formatMoney(l.openForeign)}</TableCell>
                                        <TableCell align="right">{formatMoney(l.carryingBase)}</TableCell>
                                        <TableCell align="right">{l.rate != null ? Number(l.rate).toFixed(4) : 'no rate'}</TableCell>
                                        <TableCell align="right">{formatMoney(l.currentBase)}</TableCell>
                                        <TableCell align="right">{formatMoney(l.difference)}</TableCell>
                                    </TableRow>
                                ))}
                                {exposure.lines.length === 0 && (
                                    <TableRow><TableCell colSpan={7} align="center">No open foreign-currency exposure.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            <Typography variant="h6">History</Typography>
            <Paper>
                <TableContainer>
                    <Table data-help-section="accounting.fxReval.historyTable" size="small" aria-label="Revaluation history">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="accounting.fxReval.history.col.asOf">As of</TableCell>
                                <TableCell data-help="accounting.fxReval.history.col.status">Status</TableCell>
                                <TableCell data-help="accounting.fxReval.history.col.items" align="right">Items</TableCell>
                                <TableCell data-help="accounting.fxReval.history.col.gain" align="right">Gain</TableCell>
                                <TableCell data-help="accounting.fxReval.history.col.loss" align="right">Loss</TableCell>
                                <TableCell data-help="accounting.fxReval.history.col.net" align="right">Net posted</TableCell>
                                <TableCell data-help="accounting.fxReval.history.col.entries">Entries</TableCell>
                                {canRun && <TableCell data-help="accounting.fxReval.history.col.actions" align="right">Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {runs.map(r => (
                                <TableRow key={r.id}>
                                    <TableCell>{formatDate(r.asOfDate)}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={STATUS_LABEL[r.status] ?? r.status}
                                            color={r.status === 'POSTED' ? 'success' : r.status === 'REVERSED_MANUALLY' ? 'default' : 'warning'} />
                                        {r.stale && (
                                            <Chip size="small" color="error" sx={{ ml: 0.5 }}
                                                label="stale: undo and re-run" />
                                        )}
                                    </TableCell>
                                    <TableCell align="right">{r.itemCount}</TableCell>
                                    <TableCell align="right">{formatMoney(r.totalGain)}</TableCell>
                                    <TableCell align="right">{formatMoney(r.totalLoss)}</TableCell>
                                    <TableCell align="right">{formatMoney(r.netGainLoss)}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                            <JournalLink id={r.journalEntryId} label="Revaluation" />
                                            <JournalLink id={r.reversalJournalEntryId} label="Auto-reversal" />
                                        </Box>
                                    </TableCell>
                                    {canRun && (
                                        <TableCell align="right">
                                            {r.status !== 'REVERSED_MANUALLY' && (
                                                <Button data-help="accounting.fxReval.action.undo" size="small" color="warning" disabled={busy} onClick={() => askUndo(r)}>Undo run</Button>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                            {runs.length === 0 && (
                                <TableRow><TableCell colSpan={canRun ? 8 : 7} align="center">No revaluation runs yet.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            <ConfirmDialog open={confirmRun} title="Run revaluation" confirmLabel="Run revaluation"
                message={`Post the unrealised FX revaluation as of ${formatDate(asOf)}? An automatic reversal is posted for the next day.`}
                onCancel={() => setConfirmRun(false)} onConfirm={doRun} />
            <ConfirmDialog open={!!undoTarget} title="Undo revaluation" confirmLabel="Undo run" confirmColor="warning"
                message={undoTarget ? (
                    <>
                        {`Undo the revaluation as of ${formatDate(undoTarget.asOfDate)}? Both entries are reversed.`}
                        {undoLosses.length > 0 && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                Undoing this run cannot be repeated for:
                                <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                                    {undoLosses.map(l => <li key={l}>{l}</li>)}
                                </ul>
                            </Alert>
                        )}
                    </>
                ) : ''}
                onCancel={() => setUndoTarget(null)} onConfirm={() => undoTarget && doUndo(undoTarget)} />
        </Box>
    );
};

export default FxRevaluation;
